import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BlogArchive = () => {
  const [services, setServices] = useState([]);
  const [specialisms, setSpecialisms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedSpecialism, setSelectedSpecialism] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(16);
  const [maxPage, setMaxPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false); // Add loading state

  const updateResults = async (append = false) => {
    setIsLoading(true); // Set loading state to true
    let searchString = "";
    if (selectedCategory) searchString += `&category=${selectedCategory.id}`;
    if (selectedService) searchString += `&service=${selectedService.id}`;
    if (selectedSpecialism)
      searchString += `&specialism-type=${selectedSpecialism.id}`;
    if (search) searchString += `&search=${search}`;

    const paginationParam = `&per_page=${perPage}&page=${append ? page : 1}`;

    try {
      const results = await axios.get(
        `/wp-json/gs/v1/get_posts?${searchString}${paginationParam}`
      );
      setMaxPage(+results.headers["x-wp-totalpages"]);
      if (append) {
        setPosts((prevPosts) => [...prevPosts, ...results.data]);
      } else {
        setPosts(results.data);
        setPage(1); // Reset page to 1 when doing a new search
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setIsLoading(false); // Set loading state to false when done
    }
  };

  const loadNextPage = async () => {
    if (isLoading || page >= maxPage) return; // Prevent loading if already loading or at max page
    
    const nextPage = page + 1;
    setPage(nextPage);
    
    setIsLoading(true);
    let searchString = "";
    if (selectedCategory) searchString += `&category=${selectedCategory.id}`;
    if (selectedService) searchString += `&service=${selectedService.id}`;
    if (selectedSpecialism)
      searchString += `&specialism-type=${selectedSpecialism.id}`;
    if (search) searchString += `&search=${search}`;

    try {
      const results = await axios.get(
        `/wp-json/gs/v1/get_posts?${searchString}&per_page=${perPage}&page=${nextPage}`
      );
      setPosts((prevPosts) => [...prevPosts, ...results.data]);
    } catch (error) {
      console.error("Error loading next page:", error);
      setPage(page); // Revert page increment if request failed
    } finally {
      setIsLoading(false);
    }
  };

  const handleScroll = useCallback(() => {
    if (isLoading) return; // Skip if already loading
    
    const autoloadElement = document.querySelector(".blogarchive__autoload");
    if (!autoloadElement) return;

    const rect = autoloadElement.getBoundingClientRect();
    const isVisible = 
      rect.top <= window.innerHeight && 
      rect.bottom >= 0;
      
    if (isVisible && page < maxPage) {
      loadNextPage();
    }
  }, [page, maxPage, isLoading]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesData = await axios.get(
          `/wp-json/wp/v2/categories?per_page=100`
        );
        const servicesData = await axios.get(
          `/wp-json/wp/v2/service?per_page=100`
        );
        const specialismsData = await axios.get(
          `/wp-json/wp/v2/specialism-type?per_page=100`
        );

        setCategories(categoriesData.data);
        setServices(servicesData.data);
        setSpecialisms(specialismsData.data);

        await updateResults();
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    if (
      selectedCategory !== null || 
      selectedService !== null || 
      selectedSpecialism !== null || 
      search !== ""
    ) {
      updateResults(false); // Reset results when filters change
    }
  }, [selectedCategory, selectedService, selectedSpecialism, search]);

  return (
    <section className="blogarchive">
      <div className="blogarchive__searchbar">
        <div className="container">
          <div className="row">
            <div className="col-md-6">
              <div className="input-group mb-3">
                <input
                  type="text"
                  className="form-control"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                />
                <div className="input-group-append">
                  <button
                    className="btn peoplearchive__searchbutton"
                    type="button"
                    onClick={() => updateResults()}
                  >
                    <i className="fas fa-search"></i>
                  </button>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <select
                onChange={(e) =>
                  setSelectedService(
                    services.find((service) => service.id === +e.target.value)
                  )
                }
              >
                <option value="">Service</option>
                {services.map((service, index) => (
                  <option key={`service-${index}`} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                onChange={(e) =>
                  setSelectedSpecialism(
                    specialisms.find(
                      (specialism) => specialism.id === +e.target.value
                    )
                  )
                }
              >
                <option value="">Specialism</option>
                {specialisms.map((specialism, index) => (
                  <option key={`specialism-${index}`} value={specialism.id}>
                    {specialism.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                onChange={(e) =>
                  setSelectedCategory(
                    categories.find((category) => category.id === +e.target.value)
                  )
                }
              >
                <option value="">Category</option>
                {categories.map((category, index) => (
                  <option key={`category-${index}`} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="blogarchive__results">
          {posts.map((post, index) => (
            <div className="blogarchive__post" key={`post-${index}`}>
              <a href={post.url}>
                <img src={post.image} alt="" />
                <div className="blogarchive__overlay"></div>
                <div className="blogarchive__slide"></div>
                <div className="blogarchive__content">
                  <h3 dangerouslySetInnerHTML={{ __html: post.name }}></h3>
                  <div
                    className="blogarchive__excerpt"
                    dangerouslySetInnerHTML={{ __html: post.excerpt }}
                  ></div>
                  <div
                    className="blogarchive__date"
                    dangerouslySetInnerHTML={{ __html: post.date }}
                  ></div>
                  <div className="blogarchive__readmore">Read more...</div>
                </div>
              </a>
            </div>
          ))}
        </div>
        {page < maxPage && (
          <div className="row">
            <div className="col-md-12 blogarchive__autoload">&nbsp;</div>
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogArchive;
